/**
 * SceneVisual — what's on screen while a paragraph is spoken.
 *
 * This is the piece that turns Studio from "audio with a waveform on it" into
 * something you can post. Every path reuses an engine that already ships:
 *
 *   Upload  → POST /studio/creations (kind=image) → object storage
 *   Generate→ POST /create/image      (invisible model routing + the Cast)
 *   Restyle → POST /create/edit       (edit-with-words, same as chat)
 *
 * Visuals are always referenced by a same-origin `/api/...` URL so the canvas
 * recorder can draw them without tainting the frame.
 */
import { useRef, useState } from "react";
import * as L from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const MOTIONS = [
  { id: "drift",    label: "Drift",     hint: "slow sideways move" },
  { id: "zoom_in",  label: "Zoom in",   hint: "pushes in slowly" },
  { id: "zoom_out", label: "Zoom out",  hint: "pulls back slowly" },
  { id: "still",    label: "Hold",      hint: "no movement" },
];

const absolute = (path) =>
  path?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${path}` : path;

/** Fetch one of our own files back as base64 so it can be re-edited. */
async function urlToBase64(url) {
  const res = await api.get(url.replace(/^\/api/, ""), { responseType: "blob" });
  const blob = res.data;
  const b64 = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
  return { b64, mime: blob.type || "image/png" };
}

export function SceneVisual({ index, visual, castList = [], sceneText = "", mode = "video", onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [prompt, setPrompt] = useState("");
  const [castId, setCastId] = useState("");
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
      toast.error("Pick an image or a video clip");
      return;
    }
    if (file.size > 60 * 1024 * 1024) {
      toast.error("That file is over 60 MB — try a smaller one");
      return;
    }
    setBusy("upload");
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("title", (sceneText || "Scene").slice(0, 60));
      form.append("kind", file.type.startsWith("video/") ? "video" : "image");
      const { data } = await api.post("/studio/creations", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({
        kind: file.type.startsWith("video/") ? "video" : "image",
        url: data.url,
        creation_id: data.id,
        motion: "drift",
        source: "upload",
      });
      setOpen(false);
      toast.success("Added to this scene");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't upload that");
    } finally {
      setBusy("");
    }
  };

  const generate = async () => {
    const p = (prompt || sceneText || "").trim();
    if (p.length < 3) {
      toast.error("Describe what you want to see");
      return;
    }
    setBusy("generate");
    try {
      const { data } = await api.post("/create/image", {
        prompt: p,
        size: "1024x1024",
        cast_ids: castId ? [castId] : [],
      });
      if (!data.url) throw new Error("no url");
      const member = castList.find(c => c.id === castId);
      onChange({
        kind: "image",
        url: data.url,
        creation_id: data.creation_id,
        motion: "drift",
        source: castId ? "cast" : "generated",
        cast_id: castId || null,
        cast_name: member?.name || null,
        alt: p.slice(0, 200),
      });
      setOpen(false);
      setPrompt("");
      toast.success(member ? `Made with ${member.name}` : "Scene visual ready");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Couldn't make that picture");
    } finally {
      setBusy("");
    }
  };

  /** Photo → video. Rammy picks the engine; a clip takes 1-4 minutes, so this
   *  polls the job rather than holding the request open. */
  const animate = async () => {
    setBusy("animate");
    try {
      const { b64, mime } = await urlToBase64(visual.url);
      const { data } = await api.post("/create/animate", {
        image_base64: b64,
        mime,
        prompt: (prompt || "").trim() || "a gentle, natural camera move; keep the subject steady",
        seconds: 5,
        aspect: "9:16",
      });
      toast.info(`Moving it with ${data.model_label} — ${data.model_reason}. A minute or two.`);
      const started = Date.now();
      const poll = setInterval(async () => {
        if (Date.now() - started > 8 * 60 * 1000) {
          clearInterval(poll); setBusy("");
          toast.error("That one took too long — try a shorter movement");
          return;
        }
        try {
          const r = await api.get(`/create/video/${data.job_id}`);
          if (r.data.status === "done" && r.data.url) {
            clearInterval(poll); setBusy("");
            onChange({ ...visual, kind: "video", url: r.data.url,
                       creation_id: r.data.creation_id, source: "fal" });
            setPrompt("");
            toast.success("It moves.");
          } else if (r.data.status === "error") {
            clearInterval(poll); setBusy("");
            toast.error(r.data.error || "That photo didn't want to move");
          }
        } catch (_) { /* a blip shouldn't kill the job */ }
      }, 5000);
    } catch (e) {
      setBusy("");
      const d = e?.response?.data?.detail;
      if (d?.error === "insufficient_credits") {
        toast.error(`That needs ${d.needed} credits and you have ${d.available}.`);
      } else {
        toast.error(typeof d === "string" ? d : d?.message || "Couldn't start that");
      }
    }
  };

  const restyle = async () => {    const p = (prompt || "").trim();
    if (p.length < 3) {
      toast.error("Say what to change — \"make it night\", \"remove the car\"");
      return;
    }
    setBusy("edit");
    try {
      const { b64, mime } = await urlToBase64(visual.url);
      const { data } = await api.post("/create/edit", {
        image_base64: b64, mime, prompt: p,
      });
      if (!data.url) throw new Error("no url");
      onChange({
        ...visual,
        url: data.url,
        creation_id: data.creation_id,
        source: "generated",
        alt: p.slice(0, 200),
      });
      setPrompt("");
      toast.success("Changed it");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Couldn't change that picture");
    } finally {
      setBusy("");
    }
  };

  /* ── Audio projects have nothing on screen ──────────────────── */
  if (mode === "audio") return null;

  const photoFirst = mode === "photo";

  /* ── Empty state ─────────────────────────────────────────────── */
  if (!visual?.url) {
    return (
      <div className="mt-3" data-testid={`scene-visual-${index}`}>
        {!open ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="w-full rounded-xl border border-dashed border-[var(--rammy-border)] py-2.5 text-[12px]
              text-[var(--rammy-text-muted)] hover:border-[var(--rammy-cyan)] hover:text-[var(--rammy-cyan-dark)]
              transition-colors inline-flex items-center justify-center gap-1.5"
            data-testid={`scene-visual-add-${index}`}
          >
            <L.ImagePlus size={14}/> {photoFirst ? "Add your photo" : "Add what's on screen"}
          </button>
        ) : (
          <div className="rounded-xl bg-[var(--rammy-cyan)]/8 border border-[var(--rammy-cyan)]/25 p-3 space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-cyan-dark)]">
                Scene {index + 1} visual
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)]"
                data-testid={`scene-visual-cancel-${index}`}
              >
                <L.X size={13}/>
              </button>
            </div>

            <Input
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={sceneText ? `Describe the shot — or leave blank to use this paragraph` : "Describe the shot…"}
              className="rounded-full h-9 text-[12px] border-[var(--rammy-border)]"
              data-testid={`scene-visual-prompt-${index}`}
            />

            {castList.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[10.5px] text-[var(--rammy-text-muted)]">With:</span>
                {castList.slice(0, 6).map(c => (
                  <button
                    key={c.id}
                    type="button"
                    aria-pressed={castId === c.id}
                    onClick={() => setCastId(castId === c.id ? "" : c.id)}
                    className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${
                      castId === c.id
                        ? "bg-[var(--rammy-cyan-dark)] text-white border-[var(--rammy-cyan-dark)]"
                        : "bg-[var(--rm-surface)] border-[var(--rammy-border)] text-[var(--rammy-text-sec)] hover:border-[var(--rammy-cyan)]"
                    }`}
                    data-testid={`scene-visual-cast-${index}-${c.id}`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {photoFirst ? (
                <>
                  <Button
                    onClick={() => fileRef.current?.click()}
                    disabled={!!busy}
                    className="btn-pill btn-primary h-8 px-3 text-[12px]"
                    data-testid={`scene-visual-upload-${index}`}
                  >
                    {busy === "upload"
                      ? <><L.Loader2 size={12} className="animate-spin mr-1"/> Uploading…</>
                      : <><L.Upload size={12} className="mr-1"/> Choose a photo</>}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={generate}
                    disabled={!!busy}
                    className="btn-pill h-8 px-3 text-[12px]"
                    data-testid={`scene-visual-generate-${index}`}
                  >
                    {busy === "generate"
                      ? <><L.Loader2 size={12} className="animate-spin mr-1"/> Making…</>
                      : <><L.Sparkles size={12} className="mr-1"/> Or make one</>}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={generate}
                    disabled={!!busy}
                    className="btn-pill btn-primary h-8 px-3 text-[12px]"
                    data-testid={`scene-visual-generate-${index}`}
                  >
                    {busy === "generate"
                      ? <><L.Loader2 size={12} className="animate-spin mr-1"/> Making…</>
                      : <><L.Sparkles size={12} className="mr-1"/> Make a picture</>}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => fileRef.current?.click()}
                    disabled={!!busy}
                    className="btn-pill h-8 px-3 text-[12px]"
                    data-testid={`scene-visual-upload-${index}`}
                  >
                    {busy === "upload"
                      ? <><L.Loader2 size={12} className="animate-spin mr-1"/> Uploading…</>
                      : <><L.Upload size={12} className="mr-1"/> Use my photo</>}
                  </Button>
                </>
              )}
            </div>
            <p className="text-[10.5px] text-[var(--rammy-text-muted)]">
              Pictures cost credits. Your own photo is free.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,video/mp4,video/quicktime"
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
              data-testid={`scene-visual-file-${index}`}
            />
          </div>
        )}
      </div>
    );
  }

  /* ── Filled state ────────────────────────────────────────────── */
  return (
    <div className="mt-3 rounded-xl border border-[var(--rammy-border)] overflow-hidden" data-testid={`scene-visual-${index}`}>
      <div className="flex gap-3 p-2.5">
        <div className="w-20 h-20 rounded-lg overflow-hidden bg-black/10 shrink-0 relative">
          {visual.kind === "video" ? (
            <video
              src={absolute(visual.url)}
              className="w-full h-full object-cover"
              muted
              loop
              playsInline
              data-testid={`scene-visual-thumb-${index}`}
            />
          ) : (
            <img
              src={absolute(visual.url)}
              alt={visual.alt || `Scene ${index + 1}`}
              className="w-full h-full object-cover"
              data-testid={`scene-visual-thumb-${index}`}
            />
          )}
          {visual.kind === "video" && (
            <span className="absolute bottom-1 right-1 bg-black/60 text-white rounded px-1 text-[9px]">clip</span>
          )}
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            {visual.cast_name && (
              <span
                className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-[var(--rammy-cyan)]/18 text-[var(--rammy-cyan-dark)]"
                data-testid={`scene-visual-cast-badge-${index}`}
              >
                with {visual.cast_name}
              </span>
            )}
            <span className="text-[10.5px] text-[var(--rammy-text-muted)] truncate">
              {visual.source === "upload" ? "Your photo" : visual.alt || "Generated"}
            </span>
          </div>

          {visual.kind === "image" && (
            <div className="flex flex-wrap gap-1" data-testid={`scene-visual-motion-${index}`}>
              {MOTIONS.map(m => (
                <button
                  key={m.id}
                  type="button"
                  title={m.hint}
                  aria-pressed={(visual.motion || "drift") === m.id}
                  onClick={() => onChange({ ...visual, motion: m.id })}
                  className={`px-2 py-0.5 rounded-full text-[10.5px] border transition-colors ${
                    (visual.motion || "drift") === m.id
                      ? "bg-[var(--rm-ink-900)] text-white border-[var(--rm-ink-900)]"
                      : "bg-[var(--rm-surface)] border-[var(--rammy-border)] text-[var(--rammy-text-sec)] hover:border-[var(--rammy-cyan)]"
                  }`}
                  data-testid={`scene-visual-motion-${index}-${m.id}`}
                >
                  {m.label}
                </button>
              ))}
              {/* The real thing, not a camera trick. */}
              <button
                type="button"
                onClick={animate}
                disabled={!!busy}
                title="Rammy animates the photo itself — costs credits and takes a minute or two"
                className="px-2 py-0.5 rounded-full text-[10.5px] border border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/15 text-[var(--rammy-cyan-dark)] font-medium hover:bg-[var(--rammy-cyan)]/30 transition-colors inline-flex items-center gap-1 disabled:opacity-50"
                data-testid={`scene-visual-animate-${index}`}
              >
                {busy === "animate"
                  ? <><L.Loader2 size={10} className="animate-spin"/> Moving…</>
                  : <><L.Play size={10}/> Make it move</>}
              </button>
            </div>
          )}

          <div className="flex items-center gap-1.5">
            {visual.kind === "image" && (
              <>
                <Input
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Change it with words…"
                  className="rounded-full h-7 text-[11px] border-[var(--rammy-border)] flex-1"
                  data-testid={`scene-visual-edit-prompt-${index}`}
                />
                <Button
                  variant="outline"
                  onClick={restyle}
                  disabled={!!busy}
                  className="btn-pill h-7 px-2.5 text-[11px] shrink-0"
                  data-testid={`scene-visual-edit-${index}`}
                >
                  {busy === "edit"
                    ? <L.Loader2 size={11} className="animate-spin"/>
                    : <L.Wand2 size={11}/>}
                </Button>
              </>
            )}
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-[var(--rammy-text-muted)] hover:text-red-500 shrink-0 p-1"
              aria-label="Remove this visual"
              data-testid={`scene-visual-remove-${index}`}
            >
              <L.Trash2 size={13}/>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SceneVisual;
